import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Factory, TruckIcon, Building2, BriefcaseIcon, ShoppingCart, Zap, Package, FileText, Wallet, Warehouse, Users, Boxes, Cable, Sparkles, MailOpen, Globe2, Workflow, Layers, Monitor, Presentation, TrendingUp, DollarSign, Cpu, BarChart3, Cloud, Server, Target, Award, Info, Newspaper, BookOpen, HelpCircle, Clock, CreditCard, RefreshCw } from 'lucide-react';
import { contentAPI } from '../services/api';

// Product-specific icons mapping with brand colors from official websites
const productIconMap = {
  // Manufacturing - Brand-specific themes
  'Beas Manufacturing': { icon: Boxes, color: 'from-[#1F5A8E] to-[#2B3036]' },  // Deep blue from Beas branding
  'Netronic Manufacturing': { icon: Factory, color: 'from-blue-600 to-indigo-900' },  // Modern blue from Netronic
  
  // Wholesale Distribution - Purple/Pink Produmex theme + Blue Stream
  'Produmex WMS': { icon: Warehouse, color: 'from-purple-600 to-pink-600' },
  'Produmex Scan': { icon: Package, color: 'from-purple-600 to-pink-600' },
  'Stream': { icon: TruckIcon, color: 'from-blue-700 to-sky-800' },  // Transport management
  
  // E-commerce & EPOS - Dark blue FocusPoint + Soft Blue EPoS Now
  'FocusPoint': { icon: ShoppingCart, color: 'from-slate-900 to-blue-900' },
  'EPoS Now': { icon: CreditCard, color: 'from-sky-500 to-cyan-500' },
  
  // Productivity - Green themes from Boyum
  'B1 Usability Package': { icon: Sparkles, color: 'from-green-600 to-emerald-600' },
  'B1 InterCompany': { icon: Globe2, color: 'from-green-600 to-teal-600' },
  'CRM for Outlook': { icon: MailOpen, color: 'from-green-600 to-emerald-600' },
  
  // Analytics & BI - Modern analytics theme
  'QuickStart': { icon: BarChart3, color: 'from-cyan-600 to-blue-600' },
  
  // Integrations - Indigo/Purple tech themes
  'BPA Platform': { icon: Workflow, color: 'from-indigo-600 to-violet-600' },
  'Magma': { icon: Layers, color: 'from-indigo-600 to-purple-600' }
};

const MegaMenu = ({ type, onClose }) => {
  const [products, setProducts] = useState({ manufacturing: [], wholesale: [], ecommerce: [], productivity: [], analytics: [], integrations: [] });
  const [loading, setLoading] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);

  // Prefetch products on component mount for faster megamenu opening
  useEffect(() => {
    if (type === 'products' && !productsLoaded) {
      fetchProducts();
    }
  }, [type, productsLoaded]);

  const fetchProducts = async () => {
    if (productsLoaded) return; // Don't fetch if already loaded
    
    setLoading(true);
    try {
      // Check sessionStorage cache first
      const cached = sessionStorage.getItem('products_cache');
      const cacheTime = sessionStorage.getItem('products_cache_time');
      const cacheAge = cacheTime ? Date.now() - parseInt(cacheTime) : Infinity;
      
      // Use cache if less than 5 minutes old
      if (cached && cacheAge < 300000) {
        setProducts(JSON.parse(cached));
        setProductsLoaded(true);
      } else {
        // Fetch fresh data
        const data = await contentAPI.getProducts();
        setProducts(data);
        setProductsLoaded(true);
        
        // Cache the results
        sessionStorage.setItem('products_cache', JSON.stringify(data));
        sessionStorage.setItem('products_cache_time', Date.now().toString());
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // SAP Business One megamenu structure with icons
  const sapBusinessOneMenu = {
    title: 'SAP Business One',
    sections: [
      {
        title: 'Overview',
        items: [
          { name: 'SAP B1 Showcase', description: 'Complete ERP for SMEs', icon: Presentation, path: '/sap-business-one' },
          { name: 'Why SAP B1', description: '80,000+ businesses trust us', icon: Award, path: '/sap-business-one/why' }
        ]
      },
      {
        title: 'Key Industries',
        items: [
          { name: 'Manufacturing', description: 'Production & cost control', icon: Factory, path: '/sap-business-one/industries/manufacturing' },
          { name: 'Wholesale Distribution', description: 'Logistics & fulfilment', icon: TruckIcon, path: '/sap-business-one/industries/wholesale' },
          { name: 'Professional Services', description: 'Projects & resources', icon: BriefcaseIcon, path: '/sap-business-one/industries/professional-services' }
        ]
      },
      {
        title: 'For Business Leaders',
        items: [
          { name: 'Managing Directors', description: 'Strategic growth insights', icon: Target, path: '/sap-business-one/for/managing-directors' },
          { name: 'Finance Directors', description: 'Control & compliance', icon: DollarSign, path: '/sap-business-one/for/finance-directors' },
          { name: 'IT Directors', description: 'Fast deployment', icon: Cpu, path: '/sap-business-one/for/it-directors' },
          { name: 'Operations Directors', description: 'Streamline processes', icon: BarChart3, path: '/sap-business-one/for/operations-directors' },
          { name: 'Manufacturing Directors', description: 'Shop floor control', icon: Factory, path: '/sap-business-one/for/manufacturing-directors' }
        ]
      },
      {
        title: 'Deployment',
        items: [
          { name: 'Cloud Hosting', description: 'Secure AWS infrastructure', icon: Cloud, path: '/cloud-hosting' },
          { name: 'On-Premise vs Cloud', description: 'Migration benefits', icon: Server, path: '/on-premise-vs-cloud' }
        ]
      }
    ]
  };

  // Add simplified straplines to products (2-3 words max)
  const productDescriptions = {
    'Beas Manufacturing': 'Manufacturing excellence',
    'Netronic Manufacturing': 'Cloud production',
    'Produmex WMS': 'Warehouse management',
    'Produmex Scan': 'Quick barcode scanning',
    'FocusPoint': 'AI eCommerce',
    'B1 Usability Package': 'No-code toolkit',
    'B1 InterCompany': 'Multi-company sync',
    'CRM for Outlook': 'Outlook CRM',
    'QuickStart': 'BI & Planning',
    'BPA Platform': 'Process automation',
    'Magma': 'EDI integration'
  };

  // Solutions menu with straplines
  const solutionsMenu = {
    title: 'Solutions',
    sections: [
      {
        title: 'Industry Solutions',
        items: [
          { name: 'Manufacturing', description: 'Production optimisation', icon: Factory, path: '/solutions/manufacturing' },
          { name: 'Wholesale Distribution', description: 'Logistics excellence', icon: TruckIcon, path: '/solutions/distribution' },
          { name: 'Financial Services', description: 'Advanced reporting & KPIs', icon: Building2, path: '/solutions/financial' },
          { name: 'Professional Services', description: 'Project lifecycle management', icon: BriefcaseIcon, path: '/solutions/professional' }
        ]
      },
      {
        title: 'Business Solutions',
        items: [
          { name: 'Invoice Automation', description: 'Spend control & visibility', icon: FileText, path: '/solutions/invoice-automation' },
          { name: 'Expense Management', description: 'Real-time expense tracking', icon: Wallet, path: '/solutions/expense-management' },
          { name: 'Warehouse Management', description: 'Supply chain productivity', icon: Warehouse, path: '/solutions/warehouse-management' },
          { name: 'Customer Management', description: 'Full transaction visibility', icon: Users, path: '/solutions/customer-management' }
        ]
      }
    ]
  };

  const productsMenu = {
    title: 'SAP B1 Extensions',
    sections: [
      {
        title: 'Manufacturing',
        categoryId: 'manufacturing',
        items: products.manufacturing || []
      },
      {
        title: 'Distribution & Logistics',
        categoryId: 'wholesale',
        items: [
          ...(products.wholesale || []),
          // Stream - hardcoded static page (not from database)
          { title: 'Stream', description: 'Transport Management', slug: 'stream', id: 'stream-static' }
        ]
      },
      {
        title: 'E-Commerce & EPOS',
        categoryId: 'ecommerce',
        items: [
          ...(products.ecommerce || []),
          // EPoS Now - hardcoded static page (not from database)
          { title: 'EPoS Now', description: 'EPOS for retail & hospitality', slug: 'epos-now', id: 'epos-now-static' }
        ]
      },
      {
        title: 'Productivity',
        categoryId: 'productivity',
        items: products.productivity || []
      },
      {
        title: 'Analytics & BI',
        categoryId: 'analytics',
        items: products.analytics || []
      },
      {
        title: 'Integrations',
        categoryId: 'integrations',
        items: products.integrations || []
      }
    ]
  };

  // Who We Are megamenu
  const whoWeAreMenu = {
    title: 'Who We Are',
    sections: [
      {
        title: 'Company',
        items: [
          { name: 'About Us', description: 'Our story and values', icon: Info, path: '/about' },
          { name: 'Blog/News', description: 'Latest insights', icon: Newspaper, path: '/blog' }
        ]
      },
      {
        title: 'Resources',
        items: [
          { name: 'FAQs', description: 'Common questions', icon: HelpCircle, path: '/faqs' }
        ]
      }
    ]
  };

  // Services megamenu
  const servicesMenu = {
    title: 'How We Can Help',
    sections: [
      {
        title: 'Services',
        items: [
          { name: 'Implementation', description: 'Expert SAP B1 deployment', icon: Zap, path: '/services/implementation' },
          { name: 'Support', description: 'Ongoing expert assistance', icon: Users, path: '/services/support' },
          { name: 'Why Upgrade SAP', description: 'SAP B1 upgrade services', icon: RefreshCw, path: '/why-upgrade-sap' }
        ]
      }
    ]
  };

  const menuData = type === 'products' ? productsMenu : type === 'sap-business-one' ? sapBusinessOneMenu : type === 'who-we-are' ? whoWeAreMenu : servicesMenu;

  // Determine grid columns based on number of sections
  const gridCols = menuData.sections.length === 6 ? 'lg:grid-cols-6' : menuData.sections.length === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4';

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-6`}>
      {menuData.sections.map((section, idx) => {
        return (
          <div key={idx} className="relative">
            {/* Separator line after each section except the last */}
            {idx < menuData.sections.length - 1 && (
              <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
            )}
            
            {/* Section title */}
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 pb-2 border-b border-slate-100">
              {section.title}
            </h3>
            
            <div className="space-y-1">
              {section.items.map((item, itemIdx) => {
                const productIconData = productIconMap[item.title];
                const ProductIcon = productIconData?.icon || Package;
                const iconColor = productIconData?.color || 'from-blue-500 to-cyan-500';
                const SolutionIcon = item.icon;
                // Prioritize short descriptions for products, use item.description for SAP B1 and Solutions
                const itemDescription = type === 'products' ? (productDescriptions[item.title] || item.description) : item.description;
                
                return (
                  <Link
                    key={itemIdx}
                    to={item.path || `/products/${item.slug || item.id}`}
                    onClick={onClose}
                    className="block group"
                  >
                    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gradient-to-r hover:from-sky-50 hover:to-purple-50 transition-all duration-200 hover:shadow-md border border-transparent hover:border-sky-100">
                      {type === 'products' ? (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500/10 to-purple-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-sky-500 group-hover:to-purple-500 transition-all duration-200">
                          <ProductIcon className="w-6 h-6 text-sky-600 group-hover:text-white transition-colors" />
                        </div>
                      ) : (type === 'sap-business-one' || type === 'solutions' || type === 'who-we-are') && item.icon ? (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500/10 to-purple-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-sky-500 group-hover:to-purple-500 transition-all duration-200">
                          <item.icon className="w-6 h-6 text-sky-600 group-hover:text-white transition-colors" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500/10 to-purple-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-sky-500 group-hover:to-purple-500 transition-all duration-200">
                          {SolutionIcon && <SolutionIcon className="w-6 h-6 text-sky-600 group-hover:text-white transition-colors" />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors text-sm">
                          {item.name || item.title}
                        </div>
                        {itemDescription && (
                          <div className="text-xs text-slate-500 group-hover:text-slate-600 line-clamp-1">{itemDescription}</div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MegaMenu;